import axios from 'axios';
import ImportIcon from 'imagesUwKluis/svg/import.svg';
import { useContext, useEffect, useState } from 'react';
import {NavLink, useLocation} from 'react-router-dom';
import UserContext from '../../context/UserContext';
import Box from '../shared/layouts/Box';
import Flex from '../shared/layouts/Flex';
import {useTheme} from '@emotion/react';
import {useMediaQuery} from '@dnhb/node_package';
import MenuHamburger from './MenuHamburger';
import Link from './Link';
import ExternalMenuItem from './ExternalMenuItem';
import SubMenu from './SubMenu';
import WhiteLabelContext from '../../context/WhiteLabelContext';
import {useTranslation} from 'react-i18next';
import { useIconLibrary } from '../../hooks/useIconLibrary';
import UserUtilities from "./UserUtilities";
import { useQuery } from 'react-query';

const Navigation = () => {
  const [showProducts, setShowProducts] = useState(false);
  const [showDocumentsMenu, setShowDocumentsMenu] = useState(false);
  const [showCompareData, setShowCompareData] = useState(false);
  const { t } = useTranslation();

  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const toggleMenuIsOpen = (override) => {
    setMenuIsOpen((value) => {
      const menuWillOpen = typeof override === 'boolean' ? override : !value;
      if (menuWillOpen) {
        document.body.classList.add('mobile-menu--open');
      } else {
        document.body.classList.remove('mobile-menu--open');
      }
      return menuWillOpen;
    });}

  const { changedDataWithoutSaving, user, enabledModules, setEnabledModules } = useContext(UserContext);
  const {
    Logo,
    isArgo,
    dataCompare,
    customMenuItems,
    canEditMortgage,
    canEditOrv,
    finlyAppointments,
    toolMaxMortgage
  } = useContext(WhiteLabelContext);

  const { breakpoints } = useTheme();
  const isMinDesktop = useMediaQuery(
    `(min-width: ${breakpoints.ml ?? '56.25rem'})`
  );
  const {
    CalculationIcon,
    CalendarIcon,
    DashboardIcon,
    DocumentsIcon,
    RequestIcon,
    HouseIcon,
    MessageIcon,
    ProductIcon,
    ProfileIcon,
    ContactIcon,
  } = useIconLibrary();

  const closeMenuOnMobile = () => {
    if (!isMinDesktop) {
      toggleMenuIsOpen(false);
    }
  };
  const closeSubMenus = () => {
    if (!changedDataWithoutSaving) {
      setShowProducts(false);
      setShowCompareData(false);
      setShowDocumentsMenu(false);
      closeMenuOnMobile();
    }
  };

  const {data: openRequestsCount} = useQuery(
    'OpenRequestsCount',
    async () => {
      const { data: {count = 0} } = await axios('/request/count');

      return count;
    }
  );

  useEffect(() => {
    axios
      .get('/module/enabled')
      .then((resp) => {
        setEnabledModules(resp.data);
      })
      .catch((e) => console.log(e));
  }, []);

  const location = useLocation();
  useEffect(() => {
    const menuItemsWithSubItems = ['products', 'documents', 'compare'];
    if (
      !menuItemsWithSubItems.find((menuItem) =>
        location.pathname.includes(menuItem)
      )
    ) {
      closeSubMenus();
    }
  }, [changedDataWithoutSaving]);

  useEffect(() => {
    if (isMinDesktop) {
      setMenuIsOpen(true);
    } else {
      setMenuIsOpen(false);
    }
  }, [isMinDesktop]);

  return (
    <Box>
      <Flex
        flexBasis="272px"
        flexGrow={1}
        flexDirection="column"
        borderRight={{ mobile: "none", desktop: "default" }}
        position="fixed"
        zIndex={10}
        width={{ mobile: '100%', desktop: '273px' }}
        height={{ mobile: menuIsOpen ? '100vh' : '72px' }}
        css={({ colors }) => ({
          backgroundColor: colors?.white,
        })}
      >
        <Flex
          padding={{ mobile: 'm', desktop: 'l' }}
          alignItems={{ mobile: 'center' }}
          height={{ mobile: '72px', desktop: 'initial' }}
        >
          {!isMinDesktop && (
            <MenuHamburger
              menuIsOpen={menuIsOpen}
              css={({ colors }) => ({
                'div, :after, :before': { backgroundColor: colors?.primaryFont },
              })}
              onClick={() => toggleMenuIsOpen()}
            >
              <Box />
            </MenuHamburger>
          )}
          <Flex
            width="100%"
            mr={{ mobile: "l", desktop: 'initial' }}
            paddingX={{ mobile: "s", desktop: 'initial' }}
            justifyContent={'center'}
          >
            <NavLink to="dashboard" onClick={closeSubMenus}>
              <Logo css={{ maxHeight: isMinDesktop ? '80px' : '56px' }} />
            </NavLink>
          </Flex>
        </Flex>
        {menuIsOpen && (
          <>
            <Flex
              flexDirection="column"
              justifyContent={'flex-start'}
              flexBasis="88%"
              flexGrow={1}
              css={{
                overflowY: 'auto',
                zIndex: '100',
                '&::-webkit-scrollbar': {
                  width: '5px',
                  backgroundColor: 'rgba(0,0,0,0)',
                },
                '&::-webkit-scrollbar:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.09)',
                },
                '&::-webkit-scrollbar-thumb:vertical': {
                  background: 'rgba(0,0,0,0.25)',
                },
                '&::-webkit-scrollbar-thumb:vertical:active': {
                  background: 'rgba(0,0,0,0.5)',
                },
              }}
            >
                <Link
                  to="dashboard"
                  onClick={closeSubMenus}
                  title={t("Dashboard")}
                  Icon={DashboardIcon}
                />
                <Link
                  to="profile"
                  onClick={closeSubMenus}
                  title={t("Gegevens")}
                  Icon={ProfileIcon}
                />
                {(enabledModules?.finly_client_connection && finlyAppointments) && (
                  <Link
                    to="calendar"
                    onClick={closeSubMenus}
                    title={t("Mijn afspraken")}
                    Icon={CalendarIcon}
                  />
                )}
                {toolMaxMortgage && (
                    <Link
                        to="calculations/overview"
                        onClick={closeSubMenus}
                        title={t("Mijn berekeningen")}
                        Icon={CalculationIcon}
                    />
                )}

                {enabledModules?.house && !user?.business_account && (
                  <Link
                    to="house"
                    onClick={closeSubMenus}
                    title={t("Woning")}
                    Icon={HouseIcon}
                  />
                )}

                <Link
                  to="requests"
                  onClick={closeSubMenus}
                  title={t("Verzoeken")}
                  Icon={RequestIcon}
                  counter={openRequestsCount}
                />
                { (enabledModules?.products_available || canEditOrv || canEditMortgage || enabledModules.elements_dossier) && (
                  <SubMenu
                    to={`products${!enabledModules?.products_available ? '?empty=true' : ''}`}
                    title={t("Producten")}
                    Icon={ProductIcon}
                    onClick={() => {
                      setShowCompareData(false);
                      setShowDocumentsMenu(false);
                      setShowProducts(value => !value);
                    }}
                    isOpen={showProducts}
                  >
                    {showProducts && (
                      <>
                        {enabledModules?.services && (
                          <Link
                            to="products/service"
                            onClick={closeMenuOnMobile}
                            title={t("Diensten")}
                          />
                        )}
                        {(canEditMortgage || enabledModules?.mortgage) && (
                          <Link
                            to="products/mortgage"
                            onClick={closeMenuOnMobile}
                            title={t("Hypotheek")}
                          />
                        )}
                        {enabledModules?.income_insurances && (
                          <Link
                            to="products/income-insurance"
                            onClick={closeMenuOnMobile}
                            title={t('Inkomensverzekeringen')}
                          />
                        )}
                        {(canEditOrv || enabledModules?.insurances) && (
                          <Link
                            to="products/orv"
                            onClick={closeMenuOnMobile}
                            title={t("Overlijdensrisicoverzekering")}
                          />
                        )}
                        {enabledModules?.funeral_insurances && (
                          <Link
                            to="products/funeral-insurance"
                            onClick={closeMenuOnMobile}
                            title={t('Uitvaartverzekeringen')}
                          />
                        )}
                        {(enabledModules?.elements_dossier || enabledModules?.indemnity_insurances) && (
                          <Link
                            to="products/insurances"
                            onClick={closeMenuOnMobile}
                            title={t("Verzekeringen")}
                          />
                        )}
                        {enabledModules?.capital && (
                          <Link
                            to="products/capital"
                            onClick={closeMenuOnMobile}
                            title={t("Vermogen")}
                          />
                        )}
                        {enabledModules?.consumer_credit && (
                          <Link
                            to="products/consumer-credit"
                            onClick={closeMenuOnMobile}
                            title={t("Consumptief krediet")}
                          />
                        )}
                      </>
                    )}
                  </SubMenu>
                )}
                { enabledModules?.documents && (
                  <SubMenu
                    to={enabledModules?.personal_files ? 'documents/received' : 'documents/general'}
                    title={t("Documenten")}
                    Icon={DocumentsIcon}
                    onClick={() => {
                      setShowProducts(false);
                      setShowCompareData(false);
                      setShowDocumentsMenu(value => !value);
                    }}
                    isOpen={showDocumentsMenu}
                  >
                    {showDocumentsMenu && (
                      <>
                        {enabledModules?.personal_files && (
                          <Link
                            to="documents/received"
                            onClick={closeMenuOnMobile}
                            title={t("Mijn documenten")}
                          />
                        )}
                        {enabledModules?.common_files && (
                          <Link
                            to="documents/general"
                            onClick={closeMenuOnMobile}
                            title={t("Algemeen")}
                          />
                        )}
                      </>
                    )}
                  </SubMenu>
                )}
                {(dataCompare) && (
                  <Link
                    to={`compare`}
                    title={t("Gegevens ophalen")}
                    Icon={ImportIcon}
                    onClick={() => {
                      setShowProducts(false);
                      setShowDocumentsMenu(false);
                      setShowCompareData(value => !value);
                    }}
                    isOpen={showCompareData}
                  >
                  </Link>
                )}
                {enabledModules?.messages && (
                  <Link
                    to="messages"
                    onClick={closeSubMenus}
                    title={t("Berichten")}
                    Icon={MessageIcon}
                  />
                )}
                {(!isArgo && customMenuItems?.length > 0) && customMenuItems.map(({ name, url }) => (
                  <ExternalMenuItem
                    key={url}
                    title={name}
                    to={url}
                  />
                ))}
                {enabledModules?.contact && (
                  <Link
                    to="contact"
                    onClick={closeSubMenus}
                    title={t("Contact")}
                    Icon={ContactIcon}
                  />
                )}
            </Flex>
            <UserUtilities closeSubMenus={closeSubMenus} />
          </>
        )}
      </Flex>
    </Box>
  );
};

export default Navigation;
